import { parseDriverAddress, type AnswerRefAlias } from '@agavi/blocks';

import type { TFunction } from 'i18next';

import { shownNames } from '@/lib/scenario-links';


/**
 * WHAT A CANVAS CARD SAYS ABOUT ITS DRIVEN AXIS — the sibling of `scenarioLinkLabel`.
 *
 * §8.3's rule for a scenario group is that it is invisible unless EACH END says so, and the
 * driven axis only ever said it at one end. The driven block carries a link control; the
 * driver carried nothing at all, so a teacher editing an MCQ had no way to know that three
 * blocks in a later task choose their question from its options — which is also why deleting
 * one of those options could warn in a tooltip and nowhere a sighted teacher would look.
 * Reported as "i don't see any warnings on the leading block when i'm about to delete or edit
 * options" and "linked blocks should have the same kind of tags that linked scenarios have".
 *
 * ## ANCHOR for a scenario, LINK for a driver — and that is the whole vocabulary
 *
 * The owner asked for both, and for cross-task to be told from same-task. It already is: a
 * scenario link names a block in the SAME task and a cross-task one cannot exist, while a
 * driver is always in an EARLIER task. So the two glyphs already carry that distinction and a
 * third signal for it would encode nothing new.
 *
 * What the two glyphs do NOT carry is direction, which is why each returns a label naming the
 * other end: "driven by Change Velocity" on one, "drives Numerical 2" on the other.
 */
export function drivenLinkLabel(
  block: { id: string; name?: string; data?: unknown },
  tasks: { id: string; title?: string; blocks?: { id: string; name?: string; data?: unknown }[] }[],
  aliases: readonly AnswerRefAlias[],
  t: TFunction,
): { text: string; a11y: string; goTo?: { taskId: string; blockId: string } } | null {
  const nameOf = (b: { id: string; name?: string }) => b.name || b.id;
  const all = tasks.flatMap(tk => (tk.blocks ?? []).map(b => ({ block: b, task: tk })));

  // The DRIVEN end: which earlier answer chooses my question.
  const address = parseDriverAddress((block.data as { drivenBy?: string } | undefined)?.drivenBy);
  if (address) {
    /*
     * An ALIAS names several blocks, so the pill names the ALIAS — which is the teacher's own
     * word for the set and shorter than any list of its members. An unresolvable address says
     * nothing rather than a stale name, the same rule the scenario label follows for a
     * dangling leader: that state is `orphaned`, reported by the link control itself.
     */
    if (address.kind === 'alias') {
      const alias = aliases.find(a => a.id === address.aliasId);
      if (!alias?.name) return null;
      return {
        text: alias.name,
        a11y: t('blocks.drivenVersions.drivenByLabel', { name: alias.name }),
      };
    }
    const hit = all.find(x => x.task.id === address.taskId && x.block.id === address.blockId);
    if (!hit) return null;
    return {
      /*
       * AND IT IS A WAY THERE. Owner: "because these are cross-task linkages, i think it
       * would be beneficial to make that an actual link ... i notice something is
       * misbehaving, and i can just click that link to get jumped over to the exact origin
       * of the materials controlling this question."
       *
       * Only the DRIVEN end carries one. A driver may be read by several blocks in several
       * tasks, so "go to it" has no single answer there; the one end with exactly one
       * destination is the end that gets the control.
       */
      goTo: { taskId: hit.task.id, blockId: hit.block.id },
      /*
       * THE TASK, not the block. Owner: "when you are outside of the task, just knowing that
       * it's connected to another task is sufficient, the details of which block isn't
       * important". A driver is always somewhere else, so a bare block name names something
       * the teacher cannot see from where they are standing — and the task is where they
       * would go to look. The block is still spoken, where width is not a constraint.
       */
      text: hit.task.title || hit.task.id,
      a11y: t('blocks.drivenVersions.drivenByInLabel', {
        name: nameOf(hit.block), task: hit.task.title || hit.task.id,
      }),
    };
  }

  // The DRIVER end: who chooses their question from my answer. Plural, like a leader's.
  const driven = all.filter(({ block: b }) => {
    const addr = parseDriverAddress((b.data as { drivenBy?: string } | undefined)?.drivenBy);
    if (!addr) return false;
    if (addr.kind === 'alias') {
      const alias = aliases.find(a => a.id === addr.aliasId);
      return !!alias?.members.some(m => m.blockId === block.id);
    }
    return addr.blockId === block.id;
  });
  if (driven.length === 0) return null;
  /* Tasks printed and blocks spoken, the same bargain the other end makes — and deduped,
     because three blocks in one task is one place to go. */
  const where = [...new Set(driven.map(x => x.task.title || x.task.id))];
  return {
    text: shownNames(where, t),
    a11y: t('blocks.drivenVersions.drivesLabel', {
      names: driven.map(x => nameOf(x.block)).join(', '),
    }),
  };
}
